import React from 'react';

import { ArticleListingItem } from '$partials/items';
import { Button } from '$partials/ui';
import { ModuleDispatcher } from '$partials/modules';
import ArticleSplitColumns from '$partials/modules/blocks/article-split-columns';
import { Footer, Header } from '$partials/global';
import { useAjaxListing } from '$hooks/ajax-listing';
import { AjaxListingContext, AjaxListingNextButton } from '$partials/base';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').ArticleLandingPage}
 */
const ArticleLandingPage = (props) => {
    const page = props.data.page;
    const searchView = page?.searchTerm;

    const listing = useAjaxListing({
        infinite: true,
        items: page?.items?.edges,
        page: page?.items?.pageInfo?.page,
        path: `/api/v1/listings${page?.url ?? ''}`,
        totalItems: page?.items?.total,
        totalPages: page?.items?.pageInfo?.totalPages,
        query: {
            term: page.searchTerm
        }
    });

    return (
        <>
            <Header
                {...props?.data?.header}
                articleLandingPageUrl={props?.data?.articleLandingPageUrl}
                pageType={page.__typename}/>

            <main id='main-content'>
                <div className='layout-container layout-site-outer-padding pb-8 md:pb-16'>
                    <div className='col-wrapper-breakout rounded-lg bg-surface-panel-red'>
                        <div className='py-24'>
                            {/* Header & Tag Cloud */}
                            <section className='col-padding grid md:grid-cols-2 gap-x-16'>
                                <h1 className='type-style-sans-display pb-16'>
                                    {searchView ? `“${page.searchTerm}”` : page.title}
                                </h1>

                                {page.articleTypes?.length > 0 && !searchView && (
                                    <div className='flex flex-wrap gap-4 w-8/12 md:w-full'>
                                        {page.articleTypes.map(type => {
                                            return (
                                                <Button
                                                    as='a'
                                                    className='button-outcome'
                                                    href={type.url}
                                                    key={type.id}>
                                                    {type.title}
                                                </Button>
                                            );
                                        })}
                                    </div>
                                )}
                            </section>

                            {!searchView && (
                                <>
                                    {/* Latest articles */}
                                    <div className='mt-56 col-padding'>
                                        <h2 className='type-style-sans-h2-med'>
                                            {page.latestTitle || 'Latest'}
                                        </h2>

                                        {page.latestText && (
                                            <p className='type-style-sans-h3 mt-4 w-full sm:w-7/12 md:w-5/12'>
                                                {page.latestText}
                                            </p>
                                        )}

                                        {page.latestItems.length > 0 && (
                                            <div className='mt-16'>
                                                {/* Hero article */}
                                                <ArticleListingItem
                                                    orientation='wide'
                                                    {...page.latestItems[0]}/>

                                                {/* Split column articles */}
                                                {page.latestItems.length > 1 && (
                                                    <div className='mt-16'>
                                                        <ArticleSplitColumns items={page.latestItems.slice(1)}/>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Modules */}
                                    <div className='flex flex-col mt-56 gap-y-56'>
                                        {page.modules.map((module, i) => (
                                            <ModuleDispatcher
                                                {...module}
                                                key={i + module?.id} />
                                        ))}
                                    </div>
                                </>
                            )}

                            {searchView && (
                                <div className='mt-56 col-padding flex flex-col'>
                                    <p className='type-style-sans-h3'>
                                        {listing.totalItems} Results
                                    </p>

                                    <AjaxListingContext.Provider value={listing}>
                                        <div className='grid sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-16 mt-8 md:mt-16'>
                                            {listing?.items?.map((itm, idx) => {
                                                return (
                                                    <article
                                                        aria-labelledby={itm.node.title}
                                                        aria-posinset={idx + 1}
                                                        aria-setsize={listing.items.length}
                                                        className=''
                                                        key={itm.node.id + '-' + idx}>
                                                        <ArticleListingItem {...itm.node} />
                                                    </article>
                                                );
                                            })}
                                        </div>

                                        {(listing?.items?.length > 0) ? (
                                            listing?.page !== listing?.totalPages && (
                                                <AjaxListingNextButton
                                                    aria-label='Load more articles'
                                                    className='button-outcome mt-56 self-start'>
                                                    <span>Load more...</span>
                                                </AjaxListingNextButton>
                                            )
                                        ) : (
                                            <p className='type-style-sans-body'>
                                                No articles

                                                <span className='sr-only'>
                                                Found
                                                </span>
                                            </p>
                                        )}
                                    </AjaxListingContext.Provider>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </main>

            <Footer
                {...props?.data?.footer}
                pageType={page.__typename}/>
        </>
    );
};

/* ========================================================================= */
/* Exports */
/* ========================================================================= */

export default ArticleLandingPage;
